import { encodeRegistrationResponse } from "./formatting.js";
import { base64ToArrayBuffer } from "./encoding.js";
/**
 * Web passkey registration
 * Always uses encodeRegistrationResponse for formatting
 */
export async function registerPasskey(options) {
    return await registerPasskeyWeb(options);
}
/**
 * Web passkey authentication
 * Always uses encodeAuthenticationResponse for formatting
 */
export async function authenticatePasskey(options) {
    return await authenticatePasskeyWeb(options);
}
/**
 * Check if passkeys are supported on the current platform
 */
export async function isPasskeySupported() {
    return "credentials" in navigator && "create" in navigator.credentials;
}
/**
 * Web implementation using WebAuthn API
 */
async function registerPasskeyWeb(options) {
    if (typeof navigator === "undefined" || !navigator.credentials) {
        throw new Error("WebAuthn not supported in this environment");
    }
    const challenge = base64ToArrayBuffer(options.challenge || "");
    const publicKeyOptions = {
        challenge,
        rp: {
            name: options.rpName,
            id: options.rpID,
        },
        user: {
            id: base64ToArrayBuffer(options.userID),
            name: options.userName,
            displayName: options.userName,
        },
        pubKeyCredParams: [
            {
                type: "public-key",
                alg: -7, // ES256
            },
        ],
        timeout: 60000,
        attestation: "none",
        authenticatorSelection: {
            userVerification: "preferred",
        },
        excludeCredentials: options.excludeCredentials?.map((cred) => ({
            id: base64ToArrayBuffer(cred.id),
            type: cred.type,
            transports: ["internal"],
        })),
    };
    const credential = (await navigator.credentials.create({
        publicKey: publicKeyOptions,
    }));
    if (!credential) {
        throw new Error("Passkey registration was cancelled");
    }
    const encoded = encodeRegistrationResponse(credential);
    const resultWeb = {
        credentialId: encoded.id,
        publicKey: encoded.response.publicKey,
        attestationObject: encoded.response.attestationObject,
        clientDataJSON: encoded.response.clientDataJSON,
        authenticatorAttachment: encoded.authenticatorAttachment,
        clientExtensionResults: encoded.clientExtensionResults,
        getCredentialResponse() {
            return {
                id: this.credentialId,
                rawId: this.credentialId,
                response: {
                    attestationObject: this.attestationObject,
                    clientDataJSON: this.clientDataJSON,
                    publicKey: this.publicKey,
                },
                type: "public-key",
                authenticatorAttachment: this.authenticatorAttachment,
                clientExtensionResults: this.clientExtensionResults || {},
                publicKey: this.publicKey,
            };
        },
    };
    return resultWeb;
}
/**
 * Web implementation for authentication
 */
async function authenticatePasskeyWeb(options) {
    if (typeof navigator === "undefined" || !navigator.credentials) {
        throw new Error("WebAuthn not supported in this environment");
    }
    const challenge = base64ToArrayBuffer(options.challenge || "");
    const publicKeyOptions = {
        challenge,
        rpId: options.rpID,
        allowCredentials: options.allowCredentials?.map((cred) => ({
            id: base64ToArrayBuffer(cred.id),
            type: cred.type,
            transports: ["internal"],
        })),
        userVerification: options.userVerification || "preferred",
        timeout: 60000,
    };
    const credential = (await navigator.credentials.get({
        publicKey: publicKeyOptions,
    }));
    if (!credential) {
        throw new Error("Passkey authentication was cancelled");
    }
    return {
        verified: true, // Verification is done server-side
        credentialId: credential.id,
        newCounter: 0, // Will be updated by server verification
        rawCredential: credential,
    };
}
/**
 * Helper to build a RegistrationResponseJSON object from PasskeyRegistrationResult
 */
export function getRegistrationResponse(result) {
    return {
        id: result.credentialId,
        rawId: result.credentialId,
        type: "public-key",
        authenticatorAttachment: result.authenticatorAttachment,
        clientExtensionResults: result.clientExtensionResults || {},
        response: {
            clientDataJSON: result.clientDataJSON,
            attestationObject: result.attestationObject,
            publicKey: result.publicKey,
        },
    };
}
